import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import HomePage from './pages/HomePage/HomePage';
import CollectionPage from './pages/CollectionPage/CollectionPage';
import ProductPage from './pages/ProductPage/ProductPage';
import StudioPage from './pages/StudioPage/StudioPage';
import ArchivePage from './pages/ArchivePage/ArchivePage';
import LookbookPage from './pages/LookbookPage/LookbookPage';
import TermsPage from './pages/TermsPage/TermsPage';
import PrivacyPage from './pages/PrivacyPage/PrivacyPage';
import RefundPage from './pages/RefundPage/RefundPage';
import AccessibilityPage from './pages/AccessibilityPage/AccessibilityPage';
import AboutPage from './pages/AboutPage/AboutPage';
import CartSidebar from './components/CartSidebar/CartSidebar';
import PageTransition from './components/PageTransition/PageTransition';
import PageLoader from './components/PageLoader/PageLoader';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import { Cart } from './types/shopify';
import ShopifyService from './services/shopify';
import './styles/global.scss';

function AppContent() {
  const location = useLocation();
  const [cart, setCart] = useState<Cart | null>(null);
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    // Initialize cart on app load
    const initializeCart = async () => {
      try {
        const newCart = await ShopifyService.createCart();
        setCart(newCart);
      } catch (error) {
        console.error('Failed to initialize cart:', error);
      }
    };

    initializeCart();
  }, []);

  const handleCartClick = () => {
    setIsCartOpen(true);
  };

  const handleCartClose = () => {
    setIsCartOpen(false);
  };

  const handleAddToCart = async (variantId: string, quantity: number = 1) => {
    try {
      // Если корзина не создана, создаем её
      let currentCart = cart;
      if (!currentCart) {
        console.log('Cart not found, creating new cart...');
        currentCart = await ShopifyService.createCart();
        setCart(currentCart);
      }

      if (!currentCart) {
        console.error('Failed to create cart');
        alert('Failed to add item to cart. Please try again.');
        return;
      }

      console.log('Adding to cart:', { cartId: currentCart.id, variantId, quantity });
      const updatedCart = await ShopifyService.addToCart(currentCart.id, variantId, quantity);
      console.log('Cart updated:', updatedCart);
      setCart(updatedCart);
      setIsCartOpen(true);
    } catch (error) {
      console.error('Failed to add to cart:', error);
      alert('Failed to add item to cart. Please try again.');
    }
  };

  const handleUpdateCartLine = async (lineItemId: string, quantity: number) => {
    if (!cart) return;

    try {
      const updatedCart = await ShopifyService.updateCartLine(cart.id, lineItemId, quantity);
      setCart(updatedCart);
    } catch (error) {
      console.error('Failed to update cart:', error);
    }
  };

  const handleRemoveFromCart = async (lineItemId: string) => {
    if (!cart) return;

    try {
      const updatedCart = await ShopifyService.removeFromCart(cart.id, lineItemId);
      setCart(updatedCart);
    } catch (error) {
      console.error('Failed to remove from cart:', error);
    }
  };

  // Check if current page is HomePage or Archive (full-bleed pages)
  const isHomePage = location.pathname === '/';
  const isArchivePage = location.pathname === '/archive' || location.pathname.startsWith('/archive/');

  const content = (
    <>
      <ScrollToTop />
      <Header 
        cartCount={cart?.totalQuantity || 0}
        onCartClick={handleCartClick}
      />
      
      <main className="app__main">
        <AnimatePresence mode="wait" initial={false}>
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={
              <PageTransition key="home">
                <HomePage />
              </PageTransition>
            } />
            <Route 
              path="/collections/:handle" 
              element={
                <PageTransition key={`collection-${location.pathname}`}>
                  <CollectionPage />
                </PageTransition>
              } 
            />
            <Route 
              path="/products/:handle" 
              element={
                <PageTransition key={`product-${location.pathname}`}>
                  <ProductPage 
                    onAddToCart={handleAddToCart}
                  />
                </PageTransition>
              } 
            />
            <Route path="/erd-paris" element={
              <PageTransition key="erd-paris">
                <div>ERD Paris Page Coming Soon</div>
              </PageTransition>
            } />
            <Route path="/shtrom-studio" element={
              <PageTransition key="shtrom-studio">
                <StudioPage />
              </PageTransition>
            } />
            <Route path="/account" element={
              <PageTransition key="account">
                <div>Account Page Coming Soon</div>
              </PageTransition>
            } />
            <Route path="/stockists" element={
              <PageTransition key="stockists">
                <div>Stockists Page Coming Soon</div>
              </PageTransition>
            } />
            <Route path="/contact" element={
              <PageTransition key="contact">
                <div>Contact Page Coming Soon</div>
              </PageTransition>
            } />
            <Route path="/legal" element={
              <PageTransition key="legal">
                <div>Legal Page Coming Soon</div>
              </PageTransition>
            } />
            <Route path="/terms" element={
              <PageTransition key="terms">
                <TermsPage />
              </PageTransition>
            } />
            <Route path="/privacy" element={
              <PageTransition key="privacy">
                <PrivacyPage />
              </PageTransition>
            } />
            <Route path="/refund" element={
              <PageTransition key="refund">
                <RefundPage />
              </PageTransition>
            } />
            <Route path="/accessibility" element={
              <PageTransition key="accessibility">
                <AccessibilityPage />
              </PageTransition>
            } />
            <Route path="/about" element={
              <PageTransition key="about">
                <AboutPage />
              </PageTransition>
            } />
            <Route path="/lookbook" element={
              <PageTransition key="lookbook">
                <LookbookPage />
              </PageTransition>
            } />
            <Route path="/archive" element={
              <PageTransition key="archive">
                <ArchivePage />
              </PageTransition>
            } />
            <Route path="/archive/:handle" element={
              <PageTransition key={`archive-${location.pathname}`}>
                <ArchivePage />
              </PageTransition>
            } />
          </Routes>
        </AnimatePresence>
      </main>

      <CartSidebar
        cart={cart}
        isOpen={isCartOpen}
        onClose={handleCartClose}
        onUpdateQuantity={handleUpdateCartLine}
        onRemoveItem={handleRemoveFromCart}
      />

      <Footer />
    </>
  );

  return (
    <div className={`app ${!isHomePage && !isArchivePage ? 'grid-container' : ''}`}>
      {content}
    </div>
  );
}

function App() {
  return (
    <PageLoader>
      <Router>
        <AppContent />
      </Router>
    </PageLoader>
  );
}

export default App;